// EditorHost pure helpers — the module-level, closure-free edit utilities the
// `useEditorHost` hook composes. Pure: no React, no I/O, no component state.
//
// Split out of `EditorHost/EditorHost.tsx` (C1 hotspot decomposition). These
// were already module-level in that file; lifting them here shrinks the hook
// file to (almost) the hook alone.

import { exhaustSwitchCase } from "@bilda/utils";
import type { EditorShape } from "../Editor/Palette/Palette.js";
import type { Element, ElementId, ReviewOutcome } from "../Editor/types.js";

// The insert allow-list for the current (single) template — the SAME shape the
// toolbar's Palette AND the slash menu read, so the two never disagree on what
// can be inserted. `mediaKinds` is empty: media is not an Element kind yet (the
// deferred wiring slice). Closure-free, so it lives here rather than in the hook.
export const editorShape: EditorShape = {
    rootKinds: ["heading", "prose", "objective", "mcq"],
    inlineKinds: ["strong", "emphasis", "link", "code"],
    mediaKinds: [],
};

// Two element paths are the same domain locator when their id segments match in
// order — used to match an in-editor review at an element against the pending
// proposal that targets it.
export const samePath = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((seg, i) => seg === b[i]);

// The primary editable text of a top-level block — the field the block-flow
// "Enter at end" gesture just committed. Total over Element (a new kind breaks
// it until named); used only to decide whether the anchor block's text edit
// needs persisting before the sibling insert.
export const primaryTextOf = (e: Element): string => {
    switch (e.type) {
        case "heading":
            return e.text;
        case "prose":
            return e.markdown;
        case "objective":
            return e.text;
        case "mcq":
            return e.stem;
        default:
            return exhaustSwitchCase(e);
    }
};

// The ReviewOutcome families: accepting an edit/add/remove/move takes the
// agent's change, rejecting keeps the current state. Backing a real proposal,
// the accept family routes to acceptProposal, the reject family to
// rejectProposal.
export const ACCEPT_OUTCOMES: ReadonlySet<ReviewOutcome["type"]> = new Set([
    "add",
    "remove",
    "use-new",
    "keep-both",
    "apply-move",
]);

// One choice whose correct flag moved between two element trees — what a
// correctness edit must persist. Named (never a raw pair) so the persist loop
// reads in domain vocabulary.
type ChoiceCorrectness = {
    readonly choiceId: ElementId;
    readonly correct: boolean;
};

// The choices whose `correct` flag differs between the pre- and post-edit trees.
// Pure: builds an id→correct index over the prior MCQs, then keeps the post
// choices that disagree. On a single-answer mark this yields TWO entries — the
// new key (true) and the cleared prior key (false) — each of which the host
// persists with its own keyed-merge patch. An unchanged tree yields none.
export const affectedCorrectness = (
    prev: readonly Element[],
    next: readonly Element[],
): readonly ChoiceCorrectness[] => {
    const before = new Map<ElementId, boolean>(
        prev.flatMap((e) =>
            e.type === "mcq"
                ? e.choices.map((c) => [c.id, c.correct] as const)
                : [],
        ),
    );
    return next.flatMap((e) =>
        e.type === "mcq"
            ? e.choices
                  .filter((c) => before.get(c.id) !== c.correct)
                  .map((c) => ({ choiceId: c.id, correct: c.correct }))
            : [],
    );
};
